import React, { useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import {
  LISTING_TERMS_SUMMARY,
  RENTAL_ASSET_TYPE_OPTIONS,
  RENTAL_BOOKING_OPTIONS,
  RENTAL_CANCEL_OPTIONS,
  RENTAL_INCLUDED_OPTIONS,
  RENTAL_RATE_UNIT_OPTIONS,
  RENTAL_REQUIREMENTS_OPTIONS,
  SALE_CATEGORY_OPTIONS,
  SALE_CONDITION_OPTIONS,
  SALE_CURRENCY_OPTIONS,
  SALE_FULFILLMENT_OPTIONS,
  SALE_LISTING_TYPE_OPTIONS,
  SALE_PRICE_TYPE_OPTIONS,
} from '../../config/listingPostOptions';
import {
  ASSET_KIND_OPTIONS,
  EVENT_ADMISSION_OPTIONS,
  EVENT_CATEGORY_OPTIONS,
  EVENT_FORMAT_OPTIONS,
  EVENT_TERMS_SUMMARY,
  EVENT_TICKET_DELIVERY_OPTIONS,
  EXCHANGE_MODE_OPTIONS,
  EXCHANGE_TERMS_SUMMARY,
  ONLINE_FULFILLMENT_OPTIONS,
  SETTLEMENT_CURRENCY_OPTIONS,
  SETTLEMENT_METHOD_OPTIONS,
  XCHANGE_ASSET_OPTIONS,
  XCHANGE_DIRECTION_OPTIONS,
  XCHANGE_PROOF_OPTIONS,
} from '../../config/exchangeOnlineOptions';
import { getSimpleAppExperience } from '../../config/simpleAppExperiences';
import { useActivityCart } from '../../context/ActivityCartContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { requestProgressiveAuth } from '../../util/authIntent';
import {
  fieldsSatisfied,
  resolveFormSteps,
  shouldOfferProgressive,
} from '../../util/progressiveForm';
import { CommunityAskBar } from '../CommunityChannels/CommunityChannels';
import ProgressiveFormChrome from './ProgressiveFormChrome';
import ServicesAppExperience from './ServicesAppExperience';
import PayersDeliveryAppExperience from './PayersDeliveryAppExperience';

import css from './SimpleAppExperience.module.css';
import svcCss from './ServicesAppExperience.module.css';

const STAGES = {
  explore: 'explore',
  detail: 'detail',
  create: 'create',
  review: 'review',
  success: 'success',
};

const mapSelect = opts =>
  opts.map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  }));

const OPTIONS_BY_SOURCE = {
  saleListingType: mapSelect(SALE_LISTING_TYPE_OPTIONS),
  saleCategory: SALE_CATEGORY_OPTIONS,
  saleCondition: mapSelect(SALE_CONDITION_OPTIONS),
  saleFulfillment: SALE_FULFILLMENT_OPTIONS,
  salePriceType: mapSelect(SALE_PRICE_TYPE_OPTIONS),
  saleCurrency: mapSelect(SALE_CURRENCY_OPTIONS),
  rentalAssetType: mapSelect(RENTAL_ASSET_TYPE_OPTIONS),
  rentalRateUnit: mapSelect(RENTAL_RATE_UNIT_OPTIONS),
  rentalIncluded: RENTAL_INCLUDED_OPTIONS,
  rentalRequirements: RENTAL_REQUIREMENTS_OPTIONS,
  rentalCancel: mapSelect(RENTAL_CANCEL_OPTIONS),
  rentalBooking: mapSelect(RENTAL_BOOKING_OPTIONS),
  exchangeMode: mapSelect(EXCHANGE_MODE_OPTIONS),
  assetKind: mapSelect(ASSET_KIND_OPTIONS),
  settlementMethods: SETTLEMENT_METHOD_OPTIONS,
  onlineFulfillment: ONLINE_FULFILLMENT_OPTIONS,
  settlementCurrency: mapSelect(SETTLEMENT_CURRENCY_OPTIONS),
  xchangeDirection: mapSelect(XCHANGE_DIRECTION_OPTIONS),
  xchangeAsset: mapSelect(XCHANGE_ASSET_OPTIONS),
  xchangeProof: XCHANGE_PROOF_OPTIONS,
  eventFormat: mapSelect(EVENT_FORMAT_OPTIONS),
  eventCategory: EVENT_CATEGORY_OPTIONS,
  eventAdmission: mapSelect(EVENT_ADMISSION_OPTIONS),
  eventTicketDelivery: EVENT_TICKET_DELIVERY_OPTIONS,
};

const termsCopyFor = experience => {
  if (experience?.termsSource === 'exchange') {
    return EXCHANGE_TERMS_SUMMARY;
  }
  if (experience?.termsSource === 'event') {
    return EVENT_TERMS_SUMMARY;
  }
  return LISTING_TERMS_SUMMARY;
};

const fieldLabel = (fields, id) => fields.find(f => f.id === id)?.label || id;

const resolveFieldOptions = field => {
  if (field.optionsSource && OPTIONS_BY_SOURCE[field.optionsSource]) {
    return OPTIONS_BY_SOURCE[field.optionsSource];
  }
  return (field.options || []).map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  }));
};

const optionLabel = (field, value) => {
  const opts = resolveFieldOptions(field);
  return opts.find(o => (o.value || o.id) === value)?.label || value;
};

const emptyForm = () => ({
  multiselect: {},
  termsAccepted: false,
});

const SimpleAppExperience = ({ app }) => {
  const experience = getSimpleAppExperience(app.id);
  if (experience?.layout === 'servicesTabs') {
    return <ServicesAppExperience app={app} />;
  }
  if (experience?.layout === 'payersDeliveryTabs') {
    return <PayersDeliveryAppExperience app={app} />;
  }
  return <SimpleAppExperienceDefault app={app} experience={experience} />;
};

const SimpleAppExperienceDefault = ({ app, experience }) => {
  const { upsertDraft } = useActivityCart();
  const history = useHistory();
  const location = useLocation();
  const routes = useRouteConfiguration();

  const [stage, setStage] = useState(STAGES.explore);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formStepIndex, setFormStepIndex] = useState(0);
  const [onePager, setOnePager] = useState(false);

  const multiselect = formData.multiselect || {};

  const formSteps = useMemo(
    () => resolveFormSteps(experience?.formFields || [], experience?.formSteps),
    [experience]
  );
  const progressive = shouldOfferProgressive(formSteps);
  const visibleFields =
    !progressive || onePager
      ? experience?.formFields || []
      : formSteps[formStepIndex]?.fields || [];
  const isLastFormStep = !progressive || onePager || formStepIndex >= formSteps.length - 1;

  const canAdvanceStep = useMemo(
    () => fieldsSatisfied(visibleFields, formData, multiselect),
    [visibleFields, formData, multiselect]
  );

  const canSubmitCreate = useMemo(() => {
    if (!experience?.formFields) {
      return false;
    }
    return fieldsSatisfied(experience.formFields, formData, multiselect);
  }, [experience, formData, multiselect]);

  const canFinish =
    Boolean(selected) ||
    (canSubmitCreate && (!experience.requireTerms || formData.termsAccepted));

  if (!experience) {
    return null;
  }

  const openDetail = item => {
    setSelected(item);
    setStage(STAGES.detail);
  };

  const startCreate = () => {
    setSelected(null);
    setFormData(emptyForm());
    setFormStepIndex(0);
    setOnePager(false);
    setStage(STAGES.create);
  };

  const goReviewFromCreate = () => {
    if (!canSubmitCreate) {
      return;
    }
    setStage(STAGES.review);
  };

  const advanceCreate = () => {
    if (!canAdvanceStep) {
      return;
    }
    if (!isLastFormStep) {
      setFormStepIndex(i => i + 1);
      return;
    }
    goReviewFromCreate();
  };

  const backFromCreate = () => {
    if (progressive && !onePager && formStepIndex > 0) {
      setFormStepIndex(i => i - 1);
      return;
    }
    setStage(STAGES.explore);
  };

  const goReviewFromDetail = () => {
    setStage(STAGES.review);
  };

  const toggleMulti = (fieldId, optionId) => {
    setFormData(prev => {
      const current = new Set(prev.multiselect?.[fieldId] || []);
      if (current.has(optionId)) {
        current.delete(optionId);
      } else {
        current.add(optionId);
      }
      return {
        ...prev,
        multiselect: { ...(prev.multiselect || {}), [fieldId]: [...current] },
      };
    });
  };

  const persistDemo = status => {
    const title =
      selected?.title ||
      formData.itemTitle ||
      formData.eventTitle ||
      formData.purchaseDesc?.slice(0, 48) ||
      `${app.shortName} demo`;
    upsertDraft({
      appId: app.id,
      title,
      stepIndex: 0,
      formData: selected
        ? { demoItemId: selected.id, demoTitle: selected.title }
        : { ...formData, termsAccepted: Boolean(formData.termsAccepted) },
      status,
      resumePath: `${app.hubPath}?from=landing`,
      launchHref: app.launchHref,
    });
  };

  const finishDemo = () => {
    if (experience.requireTerms && !selected && !formData.termsAccepted) {
      return;
    }
    persistDemo('completed');
    setStage(STAGES.success);
  };

  const continueWithAccount = () => {
    if (experience.requireTerms && !selected && !formData.termsAccepted) {
      return;
    }
    persistDemo('ready');
    requestProgressiveAuth({
      history,
      routes,
      location,
      intent: 'general',
      onAuthenticated: () => {
        setStage(STAGES.success);
      },
    });
  };

  const reset = () => {
    setSelected(null);
    setFormData(emptyForm());
    setFormStepIndex(0);
    setOnePager(false);
    setStage(STAGES.explore);
  };

  const renderField = field => {
    if (field.type === 'multiselect') {
      const opts = resolveFieldOptions(field);
      const selectedIds = multiselect[field.id] || [];
      return (
        <div key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <div className={svcCss.addonChips} role="group" aria-label={field.label}>
            {opts.map(opt => {
              const id = opt.id || opt.value;
              const on = selectedIds.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={on ? svcCss.addonChipActive : svcCss.addonChip}
                  aria-pressed={on}
                  onClick={() => toggleMulti(field.id, id)}
                >
                  {on ? '✓ ' : ''}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <label key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <textarea
            className={css.input}
            rows={3}
            placeholder={field.placeholder}
            value={formData[field.id] || ''}
            onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
          />
        </label>
      );
    }

    if (field.type === 'select') {
      const opts = resolveFieldOptions(field);
      return (
        <label key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <select
            className={css.input}
            value={formData[field.id] || ''}
            onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
          >
            <option value="">Choose…</option>
            {opts.map(opt => (
              <option key={opt.value || opt.id} value={opt.value || opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      );
    }

    return (
      <label key={field.id} className={css.field}>
        <span className={css.fieldLabel}>
          {field.label}
          {field.required ? ' *' : ''}
        </span>
        <input
          className={css.input}
          type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
          placeholder={field.placeholder}
          min={field.min}
          value={formData[field.id] || ''}
          onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
        />
      </label>
    );
  };

  const renderFieldReview = () =>
    experience.formFields.map(field => {
      if (field.type === 'multiselect') {
        const values = multiselect[field.id] || [];
        if (!values.length) {
          return null;
        }
        return (
          <li key={field.id} className={css.summaryRow}>
            <span className={css.summaryKey}>{fieldLabel(experience.formFields, field.id)}</span>
            <span className={css.summaryVal}>
              {values.map(v => optionLabel(field, v)).join(', ')}
            </span>
          </li>
        );
      }
      if (!formData[field.id]) {
        return null;
      }
      const display =
        field.type === 'select' ? optionLabel(field, formData[field.id]) : formData[field.id];
      return (
        <li key={field.id} className={css.summaryRow}>
          <span className={css.summaryKey}>{fieldLabel(experience.formFields, field.id)}</span>
          <span className={css.summaryVal}>{display}</span>
        </li>
      );
    });

  return (
    <div
      className={css.root}
      style={{ '--app-accent': app.accent }}
      data-testid={`simple-app-${app.id}`}
    >
      <header className={css.hero}>
        <h1 className={css.title}>{experience.headline}</h1>
        <p className={css.promise}>{experience.promise}</p>
        {experience.bullets?.length ? (
          <ul className={css.bullets}>
            {experience.bullets.map(line => (
              <li key={line} className={css.bullet}>
                {line}
              </li>
            ))}
          </ul>
        ) : null}
      </header>

      {stage === STAGES.explore ? (
        <>
          <section>
            <h2 className={css.sectionTitle}>{experience.exploreTitle}</h2>
            <p className={css.demoBanner}>Demo examples — tap to try</p>
            <div className={css.cardList}>
              {experience.demoItems.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={css.card}
                  onClick={() => openDetail(item)}
                  data-testid={`simple-app-demo-${item.id}`}
                >
                  {item.image ? (
                    <img className={css.cardMedia} src={item.image} alt="" loading="lazy" />
                  ) : null}
                  <div className={css.cardBody}>
                    <div className={css.cardTop}>
                      <h3 className={css.cardTitle}>{item.title}</h3>
                      <span className={css.badge}>{item.badge}</span>
                    </div>
                    <p className={css.cardMeta}>{item.meta}</p>
                    <p className={css.cardBlurb}>{item.blurb}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>
          <div className={css.actionRow}>
            <button
              type="button"
              className={css.primaryBtn}
              onClick={startCreate}
              data-testid="simple-app-create"
            >
              {experience.createCta}
            </button>
          </div>
          {experience.secondaryLink ? (
            <p className={css.secondaryLinkWrap}>
              <Link
                to={experience.secondaryLink.href}
                className={css.secondaryLink}
                data-testid="simple-app-secondary-link"
              >
                {experience.secondaryLink.label}
              </Link>
            </p>
          ) : null}
          <CommunityAskBar appId={app.id} />
        </>
      ) : null}

      {stage === STAGES.detail && selected ? (
        <section className={css.panel}>
          <p className={css.demoBanner}>Demo listing</p>
          {selected.image ? (
            <img className={css.detailMedia} src={selected.image} alt="" />
          ) : null}
          <h2 className={css.sectionTitle}>{selected.title}</h2>
          <p className={css.cardMeta}>{selected.meta}</p>
          <p className={css.cardBlurb}>{selected.blurb}</p>
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button type="button" className={css.secondaryBtn} onClick={() => setStage(STAGES.explore)}>
              Back
            </button>
            <button
              type="button"
              className={css.primaryBtn}
              onClick={goReviewFromDetail}
              data-testid="simple-app-interested"
            >
              I’m interested
            </button>
          </div>
        </section>
      ) : null}

      {stage === STAGES.create ? (
        <section className={css.panel}>
          <h2 className={css.sectionTitle}>{experience.createTitle}</h2>
          {progressive ? (
            <ProgressiveFormChrome
              steps={formSteps}
              stepIndex={formStepIndex}
              onePager={onePager}
              onToggleOnePager={() => {
                setOnePager(v => !v);
                setFormStepIndex(0);
              }}
              onSelectStep={setFormStepIndex}
            />
          ) : null}
          <div className={css.fields}>{visibleFields.map(renderField)}</div>
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button type="button" className={css.secondaryBtn} onClick={backFromCreate}>
              Back
            </button>
            <button
              type="button"
              className={css.primaryBtn}
              disabled={isLastFormStep ? !canSubmitCreate : !canAdvanceStep}
              onClick={advanceCreate}
              data-testid="simple-app-review"
            >
              {isLastFormStep ? 'Continue' : 'Next'}
            </button>
          </div>
        </section>
      ) : null}

      {stage === STAGES.review ? (
        <section className={css.panel}>
          <h2 className={css.sectionTitle}>Review & confirm</h2>
          <p className={css.demoBanner}>Nothing is live yet — this is a walkthrough</p>
          <ul className={css.summaryList}>
            {selected ? (
              <>
                <li className={css.summaryRow}>
                  <span className={css.summaryKey}>Choice</span>
                  <span className={css.summaryVal}>{selected.title}</span>
                </li>
                <li className={css.summaryRow}>
                  <span className={css.summaryKey}>Details</span>
                  <span className={css.summaryVal}>{selected.meta}</span>
                </li>
              </>
            ) : (
              renderFieldReview()
            )}
          </ul>
          {experience.requireTerms && !selected ? (
            <label className={svcCss.termsBox} data-testid="listing-terms">
              <input
                type="checkbox"
                checked={Boolean(formData.termsAccepted)}
                onChange={e =>
                  setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))
                }
              />
              <span>{termsCopyFor(experience)}</span>
            </label>
          ) : null}
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button
              type="button"
              className={css.secondaryBtn}
              onClick={() => setStage(selected ? STAGES.detail : STAGES.create)}
            >
              Back
            </button>
            <button
              type="button"
              className={css.primaryBtn}
              disabled={!canFinish}
              onClick={finishDemo}
              data-testid="simple-app-finish-demo"
            >
              Finish demo
            </button>
          </div>
          <button
            type="button"
            className={css.secondaryBtn}
            style={{ width: '100%', marginTop: 10 }}
            disabled={!canFinish}
            onClick={continueWithAccount}
            data-testid="simple-app-continue-account"
          >
            Continue with an account
          </button>
        </section>
      ) : null}

      {stage === STAGES.success ? (
        <section className={css.panel} data-testid="simple-app-success">
          <div className={css.successIcon} aria-hidden>
            ✓
          </div>
          <h2 className={css.sectionTitle}>{experience.successTitle}</h2>
          <p className={css.cardBlurb}>{experience.successBody}</p>
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button type="button" className={css.primaryBtn} onClick={reset}>
              Try again
            </button>
            <Link to="/landing" className={css.secondaryBtn}>
              All apps
            </Link>
          </div>
          <CommunityAskBar appId={app.id} prompt="Talk with the community" />
        </section>
      ) : null}

      <details className={css.techDetails}>
        <summary className={css.techSummary}>How it works behind the scenes</summary>
        <div className={css.techBody}>
          <ul>
            {experience.technicalNotes.map(note => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      </details>
    </div>
  );
};

export default SimpleAppExperience;
